import { useState } from 'react'
import { Loader2, Save } from 'lucide-react'

import { EditPopover } from './computedInputs'
import { dossierSaveState } from './dossierSaveState'

/**
 * Discard and Save for the unlocked dossier, in the header beside Print.
 *
 * They are laid out while the dossier is locked too — invisible, inert, out
 * of the tab order and the accessibility tree — so unlocking changes what the
 * header shows, never how wide its right-hand column is. Rendered only when
 * unlocked, they widened that column at 1366, the deal's meta line beside it
 * wrapped, and the whole dossier below moved down 19px (N3).
 *
 * The one-line reason a hand-typed figure needs (owner decision #8, nd3-05)
 * is asked for here, from Save, when the dossier hands this control the
 * reason box (`onReasonChange`). It lived only in the bottom Save bar, so the
 * bar had to open on every tab with a draft, and a bar that opens pushes the
 * tab strip up and shortens every panel above it — 53px on the first
 * keystroke into the deal's name at 1366x768, so rows that fit no longer did
 * (21 Oct review). Asked from Save, the reason takes no room until the person
 * saves, and the rule is unchanged: nothing is saved without it.
 */
export function DossierSaveControls({
  unlocked,
  dirtyCount,
  invalidCount = 0,
  reasonRequired = false,
  reasonProvided = true,
  reason = '',
  onReasonChange,
  pending,
  onDiscard,
  onSave,
}: {
  unlocked: boolean
  dirtyCount: number
  /** Entries the record cannot hold (text in an amount, an LTV over 100%).
   *  Save waits until they are fixed; each box says what is wrong (nd-062). */
  invalidCount?: number
  /** The pending changes hand-type a catalogue figure — owner decision #8
   *  requires a one-line reason before this Save can run (nd3-05). */
  reasonRequired?: boolean
  reasonProvided?: boolean
  /** The reason typed so far, and how to change it. Given, Save asks for the
   *  reason itself when one is missing; absent, Save waits (disabled) for the
   *  bottom bar's box, as it always did. */
  reason?: string
  onReasonChange?: (value: string) => void
  pending: boolean
  onDiscard: () => void
  onSave: () => void
}) {
  const [prompt, setPrompt] = useState<HTMLElement | null>(null)
  const idle = dirtyCount === 0 || pending
  const save = dossierSaveState({ dirtyCount, invalidCount, pending, reasonRequired, reasonProvided })
  // Everything else is in order and only the reason is missing: Save opens
  // the reason prompt rather than sitting disabled with the ask in a tooltip.
  const asksReason =
    Boolean(onReasonChange)
    && reasonRequired
    && !reasonProvided
    && dirtyCount > 0
    && invalidCount === 0
    && !pending
  const reasonGiven = reason.trim().length > 0
  return (
    <span
      className={`inline-flex items-center gap-2 ${unlocked ? '' : 'invisible'}`}
      aria-hidden={unlocked ? undefined : true}
    >
      <button
        type="button"
        disabled={!unlocked || idle}
        tabIndex={unlocked ? undefined : -1}
        onClick={onDiscard}
        className="inline-flex h-7 items-center rounded border border-stone-700 px-2.5 text-[11px] text-stone-300 disabled:opacity-40"
      >
        Discard
      </button>
      <button
        type="button"
        disabled={!unlocked || (save.disabled && !asksReason)}
        tabIndex={unlocked ? undefined : -1}
        title={asksReason ? 'A hand-typed figure needs a one-line reason: Save asks for it' : save.title}
        aria-haspopup={asksReason ? 'dialog' : undefined}
        aria-expanded={asksReason ? Boolean(prompt) : undefined}
        onClick={(event) => {
          if (asksReason) {
            const target = event.currentTarget
            setPrompt((current) => (current ? null : target))
            return
          }
          onSave()
        }}
        className="inline-flex h-7 items-center gap-1.5 rounded border border-amber-300/35 bg-amber-300/[0.12] px-2.5 text-[11px] font-medium text-amber-100 disabled:opacity-40"
      >
        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {pending ? 'Saving…' : dirtyCount ? `Save ${dirtyCount}` : 'Save'}
      </button>
      {prompt && onReasonChange ? (
        <EditPopover
          anchor={prompt}
          title="Reason for the hand-typed figure"
          note="Required for a figure typed by hand (an amount, a rate, a ratio). It is recorded with the change in the deal’s audit trail."
          onClose={() => setPrompt(null)}
          footer={
            <>
              <span className="text-[9px] leading-snug text-stone-600">Saved against your name.</span>
              <button
                type="button"
                disabled={!reasonGiven}
                onClick={() => {
                  setPrompt(null)
                  onSave()
                }}
                className="shrink-0 rounded border border-amber-300/35 bg-amber-300/[0.12] px-2 py-0.5 text-[10px] font-medium text-amber-100 disabled:opacity-40"
              >
                Save {dirtyCount}
              </button>
            </>
          }
        >
          <label htmlFor="dossier-header-change-reason" className="sr-only">
            Reason for the hand-typed figure (required)
          </label>
          <input
            id="dossier-header-change-reason"
            type="text"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !reasonGiven) return
              event.preventDefault()
              setPrompt(null)
              onSave()
            }}
            placeholder="e.g. T-12 line 53, trailing 12 months"
            maxLength={1000}
            className="w-full rounded border border-amber-300/30 bg-black/40 px-2 py-1 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/60 focus:outline-none"
          />
        </EditPopover>
      ) : null}
    </span>
  )
}
